import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FiSave } from "react-icons/fi";
import {
  useGetAllTenantPermissionsMutation,
  useUpdateAllTenantPermissionsMutation,
} from "../../../features/admin/permissions/allPermissionsApi";
import { Drawer, Toggle, Chip, Button, Spinner, SearchInput } from "../../../components/ui";

const token = () => sessionStorage.getItem("access_token");

// flatten the permission tree → [{ id, status }]
const flatten = (nodes = [], acc = []) => {
  nodes.forEach((n) => {
    acc.push(n);
    if (n.children?.length) flatten(n.children, acc);
  });
  return acc;
};

const collectStatuses = (nodes = [], map = {}) => {
  nodes.forEach((n) => {
    // status comes back as "1"/"0" (string) or 1/0 — coerce so active items show selected
    map[n.id] = Number(n.status) === 1 ? 1 : 0;
    if (n.children?.length) collectStatuses(n.children, map);
  });
  return map;
};

// Tenant-wise sidebar permission editor. Client is already known (passed in),
// so there is no "choose client" step — fetch its tree and toggle sections/items.
const TenantSidebarEditor = ({ tenant, onClose, onSaved }) => {
  const [getPermissions, { data, isLoading }] = useGetAllTenantPermissionsMutation();
  const [updatePermissions, { isLoading: saving }] = useUpdateAllTenantPermissionsMutation();

  const [statusMap, setStatusMap] = useState({});
  const [search, setSearch] = useState("");

  useEffect(() => {
    getPermissions({ tenant_id: tenant.tenant_id, token: token() });
  }, [getPermissions, tenant.tenant_id]);

  useEffect(() => {
    if (data?.body) setStatusMap(collectStatuses(data.body));
  }, [data]);

  const tree = data?.body || [];
  const flatNodes = useMemo(() => flatten(tree), [tree]);

  const enabledCount = useMemo(
    () => Object.values(statusMap).filter((s) => s === 1).length,
    [statusMap],
  );

  // parent toggle → cascade to every descendant
  const setSubtree = (node, value, map) => {
    map[node.id] = value;
    (node.children || []).forEach((c) => setSubtree(c, value, map));
  };

  const toggleParent = (parent, on) => {
    setStatusMap((prev) => {
      const next = { ...prev };
      setSubtree(parent, on ? 1 : 0, next);
      return next;
    });
  };

  // child toggle → parent reflects "any child enabled"
  const toggleChild = (parent, child, on) => {
    setStatusMap((prev) => {
      const next = { ...prev, [child.id]: on ? 1 : 0 };
      if (child.children?.length) setSubtree(child, on ? 1 : 0, next);
      next[parent.id] = (parent.children || []).some((c) => next[c.id] === 1) ? 1 : 0;
      return next;
    });
  };

  const setSection = (parent, on) => toggleParent(parent, on);

  const handleSave = async () => {
    const batch = flatNodes.map((n) => ({ id: n.id, status: statusMap[n.id] ?? 0 }));
    if (!batch.length) {
      toast.error("Nothing to save");
      return;
    }
    try {
      await updatePermissions({
        tenant_id: tenant.tenant_id,
        token: token(),
        data: { batch },
      }).unwrap();
      toast.success("Sidebar permissions updated");
      onSaved?.();
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  const q = search.trim().toLowerCase();
  const matches = (name) => !q || String(name || "").toLowerCase().includes(q);

  // parent shows if it OR any child matches the search
  const visibleTree = useMemo(() => {
    if (!q) return tree;
    return tree
      .map((p) => {
        if (matches(p.name)) return p;
        const kids = (p.children || []).filter((c) => matches(c.name));
        return kids.length ? { ...p, children: kids } : null;
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, q]);

  return (
    <Drawer
      width="md"
      onClose={onClose}
      title="Tenant Sidebar"
      chips={[
        { label: tenant.tenant_name, tone: "blue" },
        { label: `ID: ${tenant.tenant_id}`, tone: "gray" },
      ]}
      footer={
        <>
          <span className="text-xs text-gray-500">{enabledCount} enabled</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || isLoading}
              icon={saving ? <Spinner /> : <FiSave className="h-4 w-4" />}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </>
      }
    >
      {/* search */}
      <div className="px-6 pt-4">
        <SearchInput
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter sections & items…"
        />
      </div>

      {/* body */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {isLoading && <p className="text-sm text-gray-400 text-center py-10">Loading sections…</p>}
        {!isLoading && tree.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-10">No sidebar sections found.</p>
        )}
        {!isLoading && q && visibleTree.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-10">No matches for “{search}”.</p>
        )}

        {visibleTree.map((parent) => {
          const children = parent.children || [];
          const enabledChildren = children.filter((c) => statusMap[c.id] === 1).length;
          const parentOn = statusMap[parent.id] === 1;
          return (
            <div key={parent.id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
              {/* section header */}
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-3 min-w-0">
                  <Toggle checked={parentOn} onChange={(on) => setSection(parent, on)} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{parent.name}</p>
                    {children.length > 0 && (
                      <p className="text-xs text-gray-400">
                        {enabledChildren}/{children.length} item{children.length === 1 ? "" : "s"} enabled
                      </p>
                    )}
                  </div>
                </div>
                {children.length > 0 && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => setSection(parent, true)}
                      className="px-2 py-1 rounded-md text-xs font-medium text-blue-600 hover:bg-blue-50"
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setSection(parent, false)}
                      className="px-2 py-1 rounded-md text-xs font-medium text-gray-500 hover:bg-gray-100"
                    >
                      None
                    </button>
                  </div>
                )}
              </div>

              {/* children */}
              {children.length > 0 && (
                <div className="px-4 py-3 flex flex-wrap gap-2">
                  {children.map((child) => {
                    const on = statusMap[child.id] === 1;
                    return (
                      <Chip
                        key={child.id}
                        active={on}
                        onClick={() => toggleChild(parent, child, !on)}
                      >
                        {child.name}
                      </Chip>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Drawer>
  );
};

export default TenantSidebarEditor;
